import React from 'react';
import { AppDialog, AppDialogProps, AppDialogState } from '../Dialog/AppDialog';
import T from 'i18n-react';
import { getErrorMessageByStatus } from '../ajax';
import Fade from '@material-ui/core/Fade';
import InfiniteScroll from 'react-infinite-scroll-component';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CircularContainer } from '../Shared/Progress';
import { withSnackbar } from 'notistack';

type InfiniteDialogState = AppDialogState & {
  loaded: boolean,
  data: Array<Object>,
  count: number,
  next: boolean,
};

export type InfiniteDialogProps = AppDialogProps & {
  dataRenderer: (
    data: Array<Object>,
    state: InfiniteDialogState,
  ) => React.ReactNode,
  limit?: number,
  onFetch?: (prevState: InfiniteDialogState, data: any) => InfiniteDialogState,
};

class InfiniteDialog extends AppDialog {
  props: InfiniteDialogProps;
  state: InfiniteDialogState;

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      loaded: false,
      data: [],
      count: 1,
      next: false,
    };
    this.loading = false;
  }

  getDialogProps = () => ({
    keepMounted: true,
    ...this.props.DialogProps,
    PaperProps: {
      ...this.props.DialogProps?.PaperProps,
      style: {
        height: 'calc(100% - 64px)',
        ...this.props.DialogProps?.PaperProps?.style,
      },
    },
  });
  getCancelButtonText = () => T.translate('close');

  handleOpen(event) {
    super.handleOpen(event);

    if (!this.state.loaded && !this.loading) {
      this.loadData();
    }
  }

  loadData = () => {
    const {
      limit,
      onFetch = (prevState, data) => ({
        data: [...prevState.data, ...data],
        next: limit > 0 ? data.length >= limit : data.length > 0,
      }),
    } = this.props;
    const { count } = this.state;

    this.loading = true;

    const requestPath = this.getRequestPath();

    this.fetchData(
      requestPath +
        (requestPath.indexOf('?') > -1 ? `&page=${count}` : `?page=${count}`),
      this.getRequestInit(),
      (data) =>
        this.setState(
          (prevState) => ({
            count: prevState.count + 1,
            loaded: true,
            ...onFetch(prevState, data),
          }),
          () => (this.loading = false),
        ),
      (response, data) => {
        this.loading = false;
        [getErrorMessageByStatus(response, data, 'infinite-dialog-error')]
          .flat()
          .forEach((m) => {
            this.enqueueSnackbar(m, { variant: 'error' });
          });
      },
    );
  };

  reset = () =>
    this.setState(
      { loaded: false, data: [], count: 0, next: false },
      this.loadData,
    );

  dialogContentRenderer = () => {
    const { data = [], next, loaded } = this.state;
    const { dataRenderer, contentNode } = this.props;
    return (
      <div style={{ position: 'relative', height: '100%' }}>
        {loaded ? (
          <Fade in timeout={300}>
            <div>
              {contentNode}
              <InfiniteScroll
                next={this.loadData}
                hasMore={next}
                loader={
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <CircularProgress size={20} />
                  </div>
                }
                dataLength={data.length}
                scrollableTarget={this.getDialogContentProps().id}
                style={{ overflow: 'hidden' }}
              >
                {dataRenderer(data, this.state)}
              </InfiniteScroll>
            </div>
          </Fade>
        ) : (
          <CircularContainer
            containerProps={{
              style: {
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%',
                minHeight: 'auto',
              },
            }}
          />
        )}
      </div>
    );
  };

  actionButtonRenderer = () => undefined;
}

export default withSnackbar(InfiniteDialog);
